import { Unit } from '@philips-internal/oswald-ui-constants';
import { clamp } from '../clamp.js';
import { isEnumValid } from '../enum.js';
import { GridFunction, GridKeyword, GridOperator, GridTrackType } from './grid-enums.js';
const EMPTY_COORDINATES = {
    columnEnd: 0,
    columnStart: 0,
    rowEnd: 0,
    rowStart: 0,
};
/**
 * Builds an empty template string array based on the number of rows and columns provided.
 *
 * @param rowCount the number of rows within the template
 * @param columnCount the number of columns within the template
 */
function buildEmptyGridTemplate(rowCount, columnCount) {
    const template = [];
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        template[rowIndex] = Array(columnCount).fill('.');
    }
    return template;
}
/**
 * Builds a template string array based on area objects.
 *
 * @param areas an array of parsed grid areas
 * @param rowCount the number of rows within the template
 * @param columnCount the number of columns within the template
 */
function buildGridTemplate(areas, rowCount, columnCount) {
    const template = buildEmptyGridTemplate(rowCount, columnCount);
    for (const area of areas) {
        const { columnEnd, columnStart, rowEnd, rowStart } = area.coordinates;
        const gridRowEnd = Math.min(rowEnd, rowCount - 1);
        const gridColumnEnd = Math.min(columnEnd, columnCount - 1);
        for (let row = rowStart; row <= gridRowEnd; ++row) {
            for (let column = columnStart; column <= gridColumnEnd; ++column) {
                template[row][column] = area.name;
            }
        }
    }
    return template;
}
/**
 * Coverts a template string array to raw string.
 */
function formatGridTemplate(template) {
    return template.length > 0
        ? `"${template.map(r => r.join(' ')).join('""')}"`
        : '';
}
/**
 * Converts a track list to raw string.
 */
function formatGridTrackList(list) {
    return list.join(' ');
}
/**
 * Merges two coordinates into one.
 *
 * @param from the initial coordinate object
 * @param to the final coordinate object
 */
function mergeGridCoordinates(from, to) {
    if (!from || !to) {
        return from || to || EMPTY_COORDINATES;
    }
    const columnEnd = Math.max(from.columnEnd, to.columnEnd);
    const columnStart = Math.min(from.columnStart, to.columnStart);
    const rowEnd = Math.max(from.rowEnd, to.rowEnd);
    const rowStart = Math.min(from.rowStart, to.rowStart);
    return {
        columnEnd,
        columnStart,
        rowEnd,
        rowStart,
    };
}
/**
 * Parses a raw string into an array of areas.
 */
function parseGridAreaList(raw) {
    const areas = {};
    const template = parseGridTemplate(raw);
    for (let rowIndex = 0; rowIndex < template.length; rowIndex++) {
        const row = template[rowIndex];
        for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
            const name = row[columnIndex];
            if (name === '.') {
                continue;
            }
            if (name in areas) {
                const area = areas[name];
                area.coordinates.rowEnd = rowIndex;
                area.coordinates.columnEnd = columnIndex;
                continue;
            }
            areas[name] = {
                coordinates: {
                    columnEnd: columnIndex,
                    columnStart: columnIndex,
                    rowEnd: rowIndex,
                    rowStart: rowIndex,
                    toString() {
                        const { columnEnd, columnStart, rowEnd, rowStart } = this;
                        return `${rowStart + 1} / ${columnStart + 1} / ${rowEnd + 2} / ${columnEnd + 2}`;
                    },
                },
                name,
            };
        }
    }
    return Object.values(areas);
}
/**
 * Parses tracks information into a GridFunctionTrack.
 *
 * @param name the track name
 * @param tracks the function parameters
 * @param separator the string that separates each of the parameters when the track object is formatted
 */
function parseGridFunctionTrack(name, tracks, separator) {
    const params = tracks.map(track => parseGridTrack(track));
    return {
        name,
        params,
        toString() {
            return `${this.name}(${this.params.join(separator)})`;
        },
        type: GridTrackType.FUNCTION,
    };
}
/**
 * Parses keywords into a GridKeywordTrack.
 */
function parseGridKeywordTrack(keyword) {
    return {
        toString() {
            return this.unit;
        },
        type: GridTrackType.KEYWORD,
        unit: keyword,
    };
}
/**
 * Parses a raw string a GridLengthTrack.
 */
function parseGridLengthTrack(raw) {
    const [, value = '0', unit = Unit.PX] = raw?.match(/([\d.]+)(\w*%*)/) ?? [];
    return {
        toString() {
            return `${this.value}${this.unit}`;
        },
        type: GridTrackType.LENGTH,
        unit: unit,
        value: Number.parseFloat(value),
    };
}
/**
 * Parses a raw string parses into a template string array.
 */
function parseGridTemplate(raw) {
    if (raw) {
        return raw
            .substr(1, raw.length - 2)
            .split('""')
            .map((r = '') => r.split(' '));
    }
    return [];
}
/**
 * Parses track and words into the correct track.
 *
 * @param track the name used to identify the track object type
 * @param words the list of parameters within the track
 */
function parseGridTrack(track, words) {
    if (typeof track === 'object') {
        return track;
    }
    if (isEnumValid(GridKeyword, track) || isEnumValid(GridOperator, track)) {
        return parseGridKeywordTrack(track);
    }
    if (isEnumValid(GridFunction, track)) {
        if (track === GridFunction.MINMAX) {
            const params = shift(words, 2);
            return parseGridFunctionTrack(track, params);
        }
        if (track === GridFunction.CALC) {
            const params = shift(words, 3);
            return parseGridFunctionTrack(track, params, ' ');
        }
    }
    return parseGridLengthTrack(track);
    function shift(from = [], count) {
        const to = [];
        for (let index = 0; index < count; index++) {
            to.push(from.shift());
        }
        return to;
    }
}
/**
 * Parses a raw string into a list of tracks.
 */
function parseGridTrackList(raw) {
    const list = [];
    const words = raw?.match(/([\w|.|\-|%]+)/g) ?? [];
    while (words.length) {
        const name = words.shift();
        if (typeof name === 'string') {
            list.push(parseGridTrack(name, words));
        }
    }
    return list;
}
/**
 * Scales the values of a track up or down.
 *
 * @param track the track to be scaled
 * @param scale the proportion to which the value will be scaled
 * @param limits the minimum and maximum values allowed
 */
function scaleGridTracks(track, scale = 1, limits) {
    if (track.type === GridTrackType.FUNCTION) {
        const params = track.params.map(track => scaleGridTracks(track, scale, limits));
        return { ...track, params };
    }
    if (track.type === GridTrackType.LENGTH && track.unit !== Unit.PERCENT && isEnumValid(Unit, track.unit)) {
        const limit = limits?.[track.unit] ?? { max: Infinity, min: 0 };
        const value = clamp({
            max: limit.max,
            min: limit.min,
            value: track.value / scale,
        });
        return { ...track, value };
    }
    if (track.type === GridTrackType.KEYWORD && track.unit === GridKeyword.MIN_CONTENT) {
        return parseGridLengthTrack('30px');
    }
    return track;
}
export { buildEmptyGridTemplate, buildGridTemplate, formatGridTemplate, formatGridTrackList, mergeGridCoordinates, parseGridAreaList, parseGridFunctionTrack, parseGridKeywordTrack, parseGridLengthTrack, parseGridTemplate, parseGridTrack, parseGridTrackList, scaleGridTracks, };
//# sourceMappingURL=grid-utils.js.map